import * as React from 'react';

import { compose, withProps } from 'recompose';

import { Typography2 } from '@coursera/cds-core';
import { Box, CheckboxGroup, Pill, color } from '@coursera/coursera-ui';
import { SvgChevronRight } from '@coursera/coursera-ui/svg';

import type Team from 'bundles/author-assignment-grading/components/common/types/Team';
import withGroupMembers from 'bundles/author-assignment-grading/utils/withGroupMembers';
import type { WithGroupMembersProps } from 'bundles/author-assignment-grading/utils/withGroupMembers';
import type GroupMember from 'bundles/author-groups/models/GroupMember';
import getAnonymizedName from 'bundles/authoring/common/utils/anonymizedGradingUtils';

import _t from 'i18n!nls/author-assignment-grading';

import 'css!./__styles__/PlagiarismFlagMemberSelect';

type InputProps = {
  groupId?: string;
  team: Team;
  guiltyMembers: Array<number>;
  onUpdateGuiltyMembers: (guiltyMembers: Array<number>) => void;
  isAnonymized?: boolean | null;
};

type TeamProps = {
  teamMembers: GroupMember[];
};

type Props = InputProps & WithGroupMembersProps & TeamProps;

type State = {
  expanded: boolean;
};

/**
 * Allows selecting members of a team which are guilty of plagiarism
 */

class PlagiarismFlagMemberSelect extends React.Component<Props, State> {
  state = {
    expanded: false,
  };

  handleChange = (guiltyMembers: Array<number>) => {
    const { onUpdateGuiltyMembers } = this.props;
    onUpdateGuiltyMembers(guiltyMembers);
  };

  toggleExpand = () => {
    const { expanded } = this.state;
    this.setState({ expanded: !expanded });
  };

  render() {
    const { teamMembers, guiltyMembers, isAnonymized } = this.props;
    const { expanded } = this.state;
    return (
      <Box rootClassName="rc-PlagiarismFlagMemberSelect" flexDirection="column">
        <button type="button" className="expand-button nostyle" onClick={this.toggleExpand}>
          <Box>
            <Typography2 component="p" variant="bodyPrimary" className="cui-p-margin">
              {_t('Affected team members')}
            </Typography2>
            <Pill size="md" type="outline" rootClassName="member-pill">
              {_t('#{guiltyNumber} out of #{totalNumber}', {
                guiltyNumber: guiltyMembers.length,
                totalNumber: teamMembers.length,
              })}
            </Pill>
          </Box>
          <SvgChevronRight
            size={18}
            color={color.icon}
            style={{
              transform: expanded ? 'rotate(-90deg)' : 'rotate(90deg)',
            }}
          />
        </button>
        {expanded && (
          <Box rootClassName="member-container pii-hide">
            <CheckboxGroup
              options={teamMembers.map((member) => ({
                value: member.userId,
                label: getAnonymizedName(member, isAnonymized),
              }))}
              value={guiltyMembers}
              isVertical={true}
              onChange={this.handleChange}
            />
          </Box>
        )}
      </Box>
    );
  }
}

export default compose<Props, InputProps>(
  withGroupMembers,
  withProps<TeamProps, InputProps & WithGroupMembersProps>(({ groupMembers, team }) => {
    const teamMembers = groupMembers
      ? (team.members
          .map((memberId) => groupMembers.find((member) => member.userId === memberId))
          .filter(Boolean) as GroupMember[])
      : [];

    return { teamMembers };
  })
)(PlagiarismFlagMemberSelect);
